import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Box, Button, IconButton, SxProps, Theme } from "@mui/material";
import { ReactNode, useState } from "react";
import CustomAccordion from "@/components/CustomAccordion";
import Flex from "@/components/Flex";
import StatusChip from "@/components/StatusChip";
import Text from "@/components/Text";
import SetCosignPinModal from "@/components/app/provider/SetCosignPinModal";
import useDisclosure from "@/hooks/use-disclosure";
import { formatDate } from "@/utils/date-time-utility";
import { DEFAULT_FONTSIZE } from "@/utils/utils";

/** Account Settings section for a logged-in Co-Signer (base role Supervising MD). */
export interface CoSignerInformationProps {
  /** Providers whose signed notes route to this Co-Signer. */
  assignedProviders?: Record<string, string>;
  cosignPin?: string;
  cosignPinUpdatedAt?: string;
  sx?: SxProps<Theme>;
}

const NEW_BADGE_SEEN_KEY = "cosigner-information-seen";

const InfoRow = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <Flex gap="22px" alignItems="center" flexWrap="wrap">
    <Text
      color="#1A1A1A80"
      fontWeight="medium"
      fontSize={DEFAULT_FONTSIZE.fontSize}
      width="210px"
    >
      {label}
    </Text>
    {children}
  </Flex>
);

const CoSignerInformation = ({
  assignedProviders,
  cosignPin,
  cosignPinUpdatedAt,
  sx,
}: CoSignerInformationProps) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isPinVisible, setIsPinVisible] = useState(false);
  const [isFirstLaunch] = useState(() => {
    try {
      if (localStorage.getItem(NEW_BADGE_SEEN_KEY)) return false;
      localStorage.setItem(NEW_BADGE_SEEN_KEY, "1");
      return true;
    } catch {
      return false;
    }
  });
  const setPinDisclosure = useDisclosure();
  const providerEntries = Object.entries(assignedProviders ?? {});

  return (
    <CustomAccordion
      expanded={isExpanded}
      accordionProps={{
        onChange: (_, expanded) => setIsExpanded(expanded),
        sx,
      }}
      accordionSummeryProps={{ style: { padding: 2 } }}
      accordionSummaryContent={
        <Flex gap={1} alignItems="center" ml={2}>
          <Text fontWeight={450} color="black">
            Co-Signer Information
          </Text>
          {isFirstLaunch && (
            <StatusChip label="NEW" color="#FFFFFF" bgColor="#0B3D91" />
          )}
        </Flex>
      }
      accordionDetailsContent={
        <Flex flexDirection="column" gap="12px" ml={2} p={0.2}>
          <InfoRow label="Base Role">
            <Text>Supervising MD</Text>
          </InfoRow>

          <InfoRow label="Co-Sign PIN">
            <Flex gap="16px" alignItems="center">
              {cosignPinUpdatedAt ? (
                <>
                  <Text letterSpacing={2}>
                    {isPinVisible ? cosignPin : "••••••••"}
                  </Text>
                  <IconButton
                    size="small"
                    onClick={() => setIsPinVisible(!isPinVisible)}
                  >
                    {isPinVisible ? (
                      <VisibilityOff fontSize="small" />
                    ) : (
                      <Visibility fontSize="small" />
                    )}
                  </IconButton>
                  <Text color="#1A1A1A80">
                    Last updated {formatDate(cosignPinUpdatedAt)}
                  </Text>
                </>
              ) : (
                <Text>Not set — Required before you can co-sign.</Text>
              )}
              <Button
                variant="outlined"
                size="small"
                onClick={() => setPinDisclosure.open()}
              >
                {cosignPinUpdatedAt ? "Reset PIN" : "Set PIN"}
              </Button>
            </Flex>
          </InfoRow>

          <InfoRow label="Assigned Providers">
            {providerEntries.length ? (
              <Flex gap={1} flexWrap="wrap">
                {providerEntries.map(([uuid, name]) => (
                  <Box key={uuid}>
                    <StatusChip variant="info" label={name} />
                  </Box>
                ))}
              </Flex>
            ) : (
              <Text>-</Text>
            )}
          </InfoRow>

          <Text color="#1A1A1A80" fontSize={DEFAULT_FONTSIZE.fontSize}>
            The Co-Sign PIN is asked every time you co-sign a visit note. It is
            separate from your login password: changing one never changes the
            other. Only you can set, change or reset it.
          </Text>

          <SetCosignPinModal
            mode={cosignPinUpdatedAt ? "reset" : "set"}
            open={setPinDisclosure.opened}
            onClose={setPinDisclosure.close}
          />
        </Flex>
      }
    />
  );
};

export default CoSignerInformation;
